import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
} from '@mui/material';

import Stack from '@/components/Stack';
import { Body, Small } from '@/components/Typography';
import {
  OnboardingStepsAPI,
  AuditLogEntry,
} from '@/services/onboardingStepsAPI';

interface AuditLogsModalProps {
  open: boolean;
  workerId: number;
  onClose: () => void;
}

const AuditLogsModal = ({ open, workerId, onClose }: AuditLogsModalProps) => {
  const [logs, setLogs] = useState<AuditLogEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [workerName, setWorkerName] = useState<string>('');

  useEffect(() => {
    if (open) {
      fetchLogs();
    }
  }, [open, workerId]);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await OnboardingStepsAPI.getAuditLogs(workerId);
      if (response.success) {
        setLogs(response.data.audit_logs);
        setWorkerName(response.data.worker_name);
      }
    } catch (err) {
      setError('Failed to load audit logs');
      console.error('Error fetching audit logs:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  return (
    <Dialog fullWidth maxWidth="lg" open={open} onClose={onClose}>
      <DialogTitle>
        Onboarding Steps Audit Logs
        {workerName && (
          <Small color="lighter" style={{ display: 'block', marginTop: '4px' }}>
            {workerName}
          </Small>
        )}
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Stack style={{ justifyContent: 'center', padding: '32px' }}>
            <CircularProgress />
          </Stack>
        ) : error ? (
          <Body color="lighter">{error}</Body>
        ) : logs.length === 0 ? (
          <Body color="lighter">No audit logs found</Body>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Step</TableCell>
                  <TableCell>Change</TableCell>
                  <TableCell>Changed By</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {logs.map((log) => (
                  <TableRow key={log.id}>
                    <TableCell>
                      <Small>{formatDate(log.changed_at)}</Small>
                    </TableCell>
                    <TableCell>
                      <Body weight="semibold">{log.step_title}</Body>
                      <Small color="lighter">{log.column_name}</Small>
                    </TableCell>
                    <TableCell>
                      <Stack gap={8} style={{ alignItems: 'center' }}>
                        <Chip
                          label={log.old_value || 'null'}
                          size="small"
                          variant="outlined"
                        />
                        <Small color="lighter">→</Small>
                        <Chip
                          color="primary"
                          label={log.new_value || 'null'}
                          size="small"
                        />
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Body>{log.changed_by.name}</Body>
                      <Small color="lighter">{log.changed_by.type}</Small>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default AuditLogsModal;
